/**
 * Format FR — UN seul helper centralisé pour TOUS les affichages.
 *
 * Règle d'or : aucune chaîne montant/surface/%/téléphone ne doit être
 * construite à la main dans un composant. Tout passe par ces helpers.
 *
 * Convention FR :
 *   - 850 000 € (espaces insécables fines, JAMAIS "850K€" ni "850k€")
 *   - 7,81 % (virgule décimale, espace insécable devant %)
 *   - 145 m² (m² avec espace + exposant 2)
 *   - +33 6 12 34 56 78 (groupes de 2)
 *   - 12/05/2026 (court) ou « 12 mai 2026 » (long)
 *
 * Tabular-nums : appliquer la classe CSS `.num` (font-variant-numeric:
 * tabular-nums) sur les éléments qui affichent ces valeurs pour aligner
 * verticalement les chiffres dans les tableaux/KPIs.
 */

// ---------------------------------------------------------------------------
// Helpers internes
// ---------------------------------------------------------------------------
const toNum = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// ---------------------------------------------------------------------------
// 1. Prix / montants en euros
// ---------------------------------------------------------------------------
const PRICE_FORMATTER = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
});

const PRICE_FORMATTER_2DEC = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

/**
 * Formate un montant en euros au format FR (jamais de "K€"/"k€").
 * @param {number|string|null} value
 * @param {object} opts { decimals: 0|2, fallback: string }
 * @returns {string} ex: "850 000 €" ou "—"
 */
export const formatPriceFR = (value, opts = {}) => {
  const { decimals = 0, fallback = '—' } = opts;
  const n = toNum(value);
  if (n === null) return fallback;
  return decimals === 2 ? PRICE_FORMATTER_2DEC.format(n) : PRICE_FORMATTER.format(n);
};

/**
 * Formate un loyer mensuel. Ajoute "/mois".
 */
export const formatRentFR = (value, opts = {}) => {
  const formatted = formatPriceFR(value, opts);
  if (formatted === (opts.fallback || '—')) return formatted;
  return `${formatted}/mois`;
};

/**
 * Formate une plage de budget. Retourne null si invalide.
 * Remplace les anciens "100k - 300k€".
 */
export const formatBudgetRangeFR = (min, max) => {
  const minN = toNum(min);
  const maxN = toNum(max);
  if (minN === null && maxN === null) return null;
  if (minN !== null && maxN !== null && maxN < minN) return '—';
  if (minN !== null && maxN !== null) return `${formatPriceFR(minN)} — ${formatPriceFR(maxN)}`;
  if (minN !== null) return `À partir de ${formatPriceFR(minN)}`;
  if (maxN !== null) return `Jusqu'à ${formatPriceFR(maxN)}`;
  return null;
};

// ---------------------------------------------------------------------------
// 2. Surfaces (m²)
// ---------------------------------------------------------------------------
const NUMBER_FORMATTER = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 0,
});

const NUMBER_FORMATTER_2DEC = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
});

/**
 * Formate une surface en m².
 * @param {number|string|null} value
 * @param {object} opts { decimals: 0|2, fallback: string }
 * @returns {string} ex: "145 m²" ou "—"
 */
export const formatSurfaceFR = (value, opts = {}) => {
  const { decimals = 0, fallback = '—' } = opts;
  const n = toNum(value);
  if (n === null || n <= 0) return fallback;
  const formatted = decimals === 2 ? NUMBER_FORMATTER_2DEC.format(n) : NUMBER_FORMATTER.format(n);
  return `${formatted} m²`;
};

/**
 * Formate une plage de surface. Retourne null si invalide.
 */
export const formatSurfaceRangeFR = (min, max) => {
  const minN = toNum(min);
  const maxN = toNum(max);
  const validMin = minN !== null && minN > 0;
  const validMax = maxN !== null && maxN > 0;
  if (!validMin && !validMax) return null;
  if (validMin && validMax && maxN < minN) return '—';
  if (validMin && validMax) return `${NUMBER_FORMATTER.format(minN)} — ${NUMBER_FORMATTER.format(maxN)} m²`;
  if (validMin) return `À partir de ${NUMBER_FORMATTER.format(minN)} m²`;
  if (validMax) return `Jusqu'à ${NUMBER_FORMATTER.format(maxN)} m²`;
  return null;
};

// ---------------------------------------------------------------------------
// 3. Pourcentages
// ---------------------------------------------------------------------------
/**
 * Formate un pourcentage au format FR.
 * @param {number|string|null} value (déjà en %, ex: 7.81 pour 7,81 %)
 * @param {object} opts { decimals: 0|1|2, fallback: string }
 * @returns {string} ex: "7,81 %" ou "—"
 */
export const formatPercentFR = (value, opts = {}) => {
  const { decimals = 2, fallback = '—' } = opts;
  const n = toNum(value);
  if (n === null) return fallback;
  const formatter = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  // Espace insécable devant le % (\u00A0)
  return `${formatter.format(n)}\u00A0%`;
};

// ---------------------------------------------------------------------------
// 4. Téléphones (international via libphonenumber-js)
// ---------------------------------------------------------------------------
import { parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js';

/**
 * Formate un numéro de téléphone international au format propre du pays détecté.
 *
 * Comportement :
 *   - Si le numéro est en E.164 (`+33612345678`, `+447911123456`) →
 *     formaté selon les conventions du pays détecté
 *     ex: `+33 6 12 34 56 78`, `+44 7911 123456`
 *   - Si le numéro est local sans indicatif (`0612345678`, `06 12 34 56 78`) →
 *     assumé pays par défaut (FR), formaté en INTERNATIONAL
 *   - Si invalide/parsable → retourne la chaîne d'origine inchangée
 *
 * @param {string|null} raw Le numéro brut (E.164 ou local)
 * @param {string} defaultCountry Code ISO du pays par défaut (ex: 'FR', 'BE', 'US')
 * @returns {string} numéro formaté ou chaîne d'origine
 */
export const formatPhoneFR = (raw, defaultCountry = 'FR') => {
  if (!raw) return '';
  try {
    const phone = parsePhoneNumberFromString(String(raw), defaultCountry);
    if (phone && phone.isValid()) {
      return phone.formatInternational();
    }
    return raw;
  } catch {
    return raw;
  }
};

/**
 * Alias plus explicite pour la nouvelle convention multi-pays.
 * @deprecated Préférer `formatPhone` dans les nouveaux composants.
 */
export const formatPhone = formatPhoneFR;

/**
 * Normalise un numéro en E.164 pour le stockage backend.
 * À appeler avant l'envoi au backend (POST/PUT contact).
 *
 * @param {string|null} raw
 * @param {string} defaultCountry
 * @returns {string|null} E.164 (ex: "+33612345678") ou null si invalide
 */
export const toE164 = (raw, defaultCountry = 'FR') => {
  if (!raw) return null;
  try {
    const phone = parsePhoneNumberFromString(String(raw), defaultCountry);
    if (phone && phone.isValid()) {
      return phone.number; // format E.164
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Détecte le pays (code ISO) à partir d'un numéro E.164 ou local.
 * Utile pour pré-sélectionner le drapeau dans le dropdown.
 *
 * @param {string|null} raw
 * @param {string} defaultCountry
 * @returns {string} code ISO ('FR', 'BE', etc.)
 */
export const detectCountry = (raw, defaultCountry = 'FR') => {
  if (!raw) return defaultCountry;
  try {
    const phone = parsePhoneNumberFromString(String(raw), defaultCountry);
    return (phone && phone.country) || defaultCountry;
  } catch {
    return defaultCountry;
  }
};

/**
 * Helper pour le composant <PhoneInput> : formate au fur et à mesure de la saisie.
 */
export const formatAsYouType = (input, country = 'FR') => {
  if (!input) return '';
  try {
    return new AsYouType(country).input(input);
  } catch {
    return input;
  }
};

// ---------------------------------------------------------------------------
// 5. Dates FR
// ---------------------------------------------------------------------------
const DATE_FORMATTER_SHORT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const DATE_FORMATTER_LONG = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const DATETIME_FORMATTER = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Formate une date au format FR court : 12/05/2026
 */
export const formatDateFR = (value, opts = {}) => {
  const { fallback = '—' } = opts;
  if (!value) return fallback;
  try {
    return DATE_FORMATTER_SHORT.format(new Date(value));
  } catch {
    return fallback;
  }
};

/**
 * Formate une date au format FR long : 12 mai 2026
 */
export const formatDateLongFR = (value, opts = {}) => {
  const { fallback = '—' } = opts;
  if (!value) return fallback;
  try {
    return DATE_FORMATTER_LONG.format(new Date(value));
  } catch {
    return fallback;
  }
};

/**
 * Formate date + heure : 12/05/2026 14:23
 */
export const formatDateTimeFR = (value, opts = {}) => {
  const { fallback = '—' } = opts;
  if (!value) return fallback;
  try {
    return DATETIME_FORMATTER.format(new Date(value));
  } catch {
    return fallback;
  }
};

// ---------------------------------------------------------------------------
// 6. Nombres simples (sans unité)
// ---------------------------------------------------------------------------
/**
 * Formate un nombre au format FR : 1 234 567
 */
export const formatNumberFR = (value, opts = {}) => {
  const { decimals = 0, fallback = '—' } = opts;
  const n = toNum(value);
  if (n === null) return fallback;
  return decimals === 0 ? NUMBER_FORMATTER.format(n) : NUMBER_FORMATTER_2DEC.format(n);
};

// ---------------------------------------------------------------------------
// 7. Adresse (chaîne OU objet legacy)
// ---------------------------------------------------------------------------
/**
 * 🔴 Certains biens portent `informations_confidentielles.adresse` sous forme d'OBJET
 * (`{ rue, code_postal, ville, pays }`) au lieu d'une chaîne. Rendu tel quel en JSX, React
 * lève « Objects are not valid as a React child » et la fiche entière devient blanche.
 * Cette fonction accepte les deux formes et ne renvoie jamais qu'une chaîne.
 */
export const formatAdresse = (value, opts = {}) => {
  const { fallback = '' } = opts;
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value !== 'object') return String(value);
  const morceaux = [value.rue || value.street, value.code_postal || value.postal_code || value.zip,
                    value.ville || value.city].filter(Boolean);
  return morceaux.join(' ') || fallback;
};
